// Google extraction error handling

import type { GoogleExtractionErrorCode } from './types';

/**
 * Google extraction error with typed code
 */
export class GoogleExtractionError extends Error {
  public readonly code: GoogleExtractionErrorCode;

  constructor(code: GoogleExtractionErrorCode, message?: string) {
    super(message || code);
    this.name = 'GoogleExtractionError';
    this.code = code;
  }
}

/**
 * Error message and suggestion for each error code
 */
export interface ErrorDetails {
  message: string;
  suggestion?: string;
  helpUrl?: string;
}

/**
 * Help URL for Google sharing settings
 */
export const GOOGLE_SHARING_HELP_URL = 'https://support.google.com/docs/answer/2494822';

/**
 * Error messages for each error code
 * Note: These are default English messages. Use i18n keys in UI.
 */
export const ERROR_MESSAGES: Record<GoogleExtractionErrorCode, ErrorDetails> = {
  PRIVATE_DOCUMENT: {
    message: 'This document is private.',
    suggestion: "Please set sharing to 'Anyone with the link' and try again.",
    helpUrl: GOOGLE_SHARING_HELP_URL,
  },
  NOT_FOUND: {
    message: 'Document not found.',
    suggestion: 'Please check the URL and try again.',
  },
  RATE_LIMITED: {
    message: 'Too many requests.',
    suggestion: 'Please wait a moment and try again.',
  },
  TIMEOUT: {
    message: 'The document took too long to load.',
    suggestion: 'Please try again.',
  },
  EXTRACTION_FAILED: {
    message: 'Could not extract content from this document.',
    suggestion: 'Try a different document or paste the content directly.',
  },
  INVALID_URL: {
    message: 'Invalid Google document URL.',
    suggestion: 'Please provide a valid Google Sheets or Google Docs link.',
  },
};

/**
 * Gets error details for a given error code
 */
export function getErrorDetails(code: GoogleExtractionErrorCode): ErrorDetails {
  return ERROR_MESSAGES[code];
}

/**
 * Creates a structured error response for API
 */
export function createErrorResponse(code: GoogleExtractionErrorCode) {
  const details = getErrorDetails(code);
  return {
    error: {
      code,
      message: details.message,
      suggestion: details.suggestion,
      helpUrl: details.helpUrl,
    },
  };
}
